import { useQuery, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import type { SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import {
  fetchEventById,
  fetchEventList,
  fetchEventDashboardCounts,
  fetchEventCustomerTotalSummary,
  fetchPmSpOptions,
  type EventCustomerTotalSummary,
  type EventCustomerTotalSummaryFilters,
  type EventDashboardCountRow,
  type EventListFilters,
  type EventListRow,
} from '../../data/events';
import { fetchLocations, fetchOrderDocSequences, type DocSequenceRow, type LocationRow } from '../../data/locations';
import supabaseClient from '../../lib/supabaseClient';
import type { EventDetail } from './index';

export const eventsListQueryKey = (filters?: EventListFilters): QueryKey => [
  'events',
  'list',
  filters?.search ?? '',
  filters?.sortBy ?? 'latest',
  filters?.sortDir ?? 'desc',
  filters?.offset ?? 0,
  filters?.pageSize ?? filters?.limit ?? 50,
  filters?.siteLocationId ?? null,
  filters?.siteLocationIds ?? null,
  filters?.statusFilter ?? 'all',
  filters?.startDateFrom ?? null,
  filters?.startDateTo ?? null,
  filters?.endDateFrom ?? null,
  filters?.endDateTo ?? null,
  filters?.pmProfileIds ?? null,
  filters?.spAuthIds ?? null,
  filters?.skipTotals ?? false,
  filters?.calendarWindowStart ?? null,
  filters?.calendarWindowEnd ?? null,
  filters?.includeWarehouseTasksInCalendar ?? true,
  filters?.calendarVisibility ?? 'visible',
  filters?.overdueOnly ?? false,
  filters?.includeFlagCounts ?? false,
  filters?.urgentOnly ?? false,
  filters?.customerPickup ?? null,
];

type UseEventsListOptions = {
  filters?: EventListFilters;
  supabase?: SupabaseClient;
  enabled?: boolean;
};

export const getEventsListQueryOptions = ({
  filters,
  supabase = supabaseClient,
  enabled = true,
}: UseEventsListOptions) => ({
  queryKey: eventsListQueryKey(filters),
  enabled,
  queryFn: async () => {
    const { data, count, error, flagCounts } = await fetchEventList(filters, { supabase });
    if (error) throw error;
    return { data, count, flagCounts };
  },
});

export const useEventsList = (
  options: UseEventsListOptions,
): UseQueryResult<{ data: EventListRow[]; count: number | null; flagCounts?: { overdue: number; urgent: number } }, PostgrestError> =>
  useQuery(getEventsListQueryOptions(options));

export const eventCustomerTotalSummaryQueryKey = (filters?: EventCustomerTotalSummaryFilters): QueryKey => [
  'events',
  'customer-total-summary',
  filters?.search ?? '',
  filters?.startDateFrom ?? null,
  filters?.startDateTo ?? null,
  filters?.endDateFrom ?? null,
  filters?.endDateTo ?? null,
  filters?.pmProfileIds ?? null,
  filters?.spAuthIds ?? null,
  filters?.overdueOnly ?? false,
  filters?.urgentOnly ?? false,
  filters?.siteLocationId ?? null,
  filters?.siteLocationIds ?? null,
  filters?.statusFilter ?? 'all',
  filters?.calendarWindowStart ?? null,
  filters?.calendarWindowEnd ?? null,
  filters?.includeWarehouseTasksInCalendar ?? true,
  filters?.calendarVisibility ?? 'visible',
  filters?.customerPickup ?? null,
];

type UseEventCustomerTotalSummaryOptions = {
  filters?: EventCustomerTotalSummaryFilters;
  supabase?: SupabaseClient;
  enabled?: boolean;
};

export const useEventCustomerTotalSummary = ({
  filters,
  supabase = supabaseClient,
  enabled = true,
}: UseEventCustomerTotalSummaryOptions): UseQueryResult<EventCustomerTotalSummary, PostgrestError> =>
  useQuery({
    queryKey: eventCustomerTotalSummaryQueryKey(filters),
    enabled,
    queryFn: async () => {
      const { data, error } = await fetchEventCustomerTotalSummary(filters, { supabase });
      if (error) throw error;
      return data;
    },
  });

export const eventDetailQueryKey = (eventId: string | null | undefined): QueryKey => [
  'events',
  'detail',
  eventId ?? null,
];

type UseEventOptions = {
  eventId: string | null | undefined;
  supabase?: SupabaseClient;
  enabled?: boolean;
};

export const getEventQueryOptions = ({
  eventId,
  supabase = supabaseClient,
  enabled = true,
}: UseEventOptions) => ({
  queryKey: eventDetailQueryKey(eventId),
  enabled: enabled && Boolean(eventId),
  queryFn: async () => {
    const { data, error } = await fetchEventById(eventId as string, { supabase });
    if (error) throw error;
    return data;
  },
});

export const useEvent = (
  options: UseEventOptions,
): UseQueryResult<EventDetail | null, PostgrestError> => useQuery(getEventQueryOptions(options));

export const locationsQueryKey = (allowedLocationIdsKey: string): QueryKey => [
  'locations',
  'list',
  allowedLocationIdsKey,
];

type UseLocationsOptions = {
  allowedLocationIds?: string[] | null;
  supabase?: SupabaseClient;
  enabled?: boolean;
};

export const getLocationsQueryOptions = ({
  allowedLocationIds,
  supabase = supabaseClient,
  enabled = true,
}: UseLocationsOptions) => {
  const allowedKey = Array.isArray(allowedLocationIds) ? allowedLocationIds.filter(Boolean).join(',') : '';
  return {
    queryKey: locationsQueryKey(allowedKey),
    enabled,
    queryFn: async () => {
      const { data, error } = await fetchLocations(allowedLocationIds, { supabase });
      if (error) throw error;
      return data;
    },
  };
};

export const useLocations = (
  options: UseLocationsOptions,
): UseQueryResult<LocationRow[], PostgrestError> => useQuery(getLocationsQueryOptions(options));

export const orderDocSequencesQueryKey = (locationIdsKey: string): QueryKey => [
  'doc_sequences',
  'order',
  locationIdsKey,
];

type UseOrderDocSequencesOptions = {
  locationIds: string[];
  supabase?: SupabaseClient;
  enabled?: boolean;
};

export const getOrderDocSequencesQueryOptions = ({
  locationIds,
  supabase = supabaseClient,
  enabled = true,
}: UseOrderDocSequencesOptions) => {
  const locationKey = Array.isArray(locationIds) ? locationIds.filter(Boolean).join(',') : '';
  return {
    queryKey: orderDocSequencesQueryKey(locationKey),
    enabled: enabled && locationIds.length > 0,
    queryFn: async () => {
      const { data, error } = await fetchOrderDocSequences(locationIds, { supabase });
      if (error) throw error;
      return data;
    },
  };
};

export const useOrderDocSequences = (
  options: UseOrderDocSequencesOptions,
): UseQueryResult<DocSequenceRow[], PostgrestError> => useQuery(getOrderDocSequencesQueryOptions(options));

/** Distinct PM and SP users assigned to events (for filter dropdowns). */
export type PmSpOption = { id: string; name: string };
export type PmSpOptions = { pmOptions: PmSpOption[]; spOptions: PmSpOption[] };

export const usePmSpOptions = (): UseQueryResult<PmSpOptions, PostgrestError> =>
  useQuery({
    queryKey: ['events', 'pmSpOptions'],
    queryFn: async () => fetchPmSpOptions(),
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });

/* ── Event dashboard counts (hero cards + leaderboard) ── */

export type EventDashboardTotals = {
  total: number;
  upcoming: number;
  inProgress: number;
  completed: number;
  completedAmount: number;
  byLocation: EventDashboardCountRow[];
};

export const useEventDashboardCounts = (
  options?: { supabase?: SupabaseClient; enabled?: boolean },
): UseQueryResult<EventDashboardTotals, PostgrestError> =>
  useQuery({
    queryKey: ['events', 'dashboardCounts'],
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000, // cache 60s to avoid concurrent-user pressure
    queryFn: async () => {
      const supabase = options?.supabase ?? supabaseClient;
      const { data, error } = await fetchEventDashboardCounts({ supabase });
      if (error) throw error;
      let total = 0;
      let upcoming = 0;
      let inProgress = 0;
      let completed = 0;
      let completedAmount = 0;
      for (const row of data) {
        total += Number(row.total) || 0;
        upcoming += Number(row.upcoming) || 0;
        inProgress += Number(row.in_progress) || 0;
        completed += Number(row.completed) || 0;
        completedAmount += Number(row.completed_amount) || 0;
      }
      return { total, upcoming, inProgress, completed, completedAmount, byLocation: data };
    },
  });
